/// <reference types="vite/client" />
import { useLingui } from "@lingui/react/macro";
import * as Sentry from "@sentry/tanstackstart-react";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
	createRootRouteWithContext,
	HeadContent,
	Outlet,
	redirect,
	Scripts,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import type * as React from "react";
import { useEffect } from "react";
import { ErrorComponent } from "~/components/error-component";
import { NotFound } from "~/components/not-found";
import { getPreferencesServerFn, PreferencesProvider } from "~/components/preferences-provider";
import { Toaster } from "~/components/ui/sonner";
import { useSession } from "~/hooks/use-session";
import { sessionQueryOptions } from "~/modules/auth/functions/session";
import { seo } from "~/modules/page/seo";
import type { AppContext } from "~/router";
import { isTRPCClientError } from "~/trpc/client";
import appCss from "~/styles/app.css?url";

export const Route = createRootRouteWithContext<AppContext>()({
	beforeLoad: async ({ context, location }) => {
		// Support legacy reset password token
		const resetPasswordTokenMatch = location.pathname.match(/^\/reset-password\/([^/]+)$/);

		if (resetPasswordTokenMatch) {
			throw redirect({
				to: "/auth/reset-password",
				search: { token: resetPasswordTokenMatch[1] },
			});
		}

		const [session] = await Promise.all([
			context.queryClient.ensureQueryData(sessionQueryOptions()),
		]);

		if (session.user) {
			Sentry.setUser({
				id: session.user.email,
				email: session.user.email,
				username: session.user.name,
				impersonation: !!session.user.impersonation,
			});
		} else {
			Sentry.setUser(null);
		}

		return { session };
	},
	loader: async ({ context }) => {
		const preferences = await getPreferencesServerFn();

		if (context.session.user) {
			Sentry.setUser({
				id: context.session.user.email,
				email: context.session.user.email,
				username: context.session.user.name,
				impersonation: !!context.session.user.impersonation,
			});
		} else {
			Sentry.setUser(null);
		}

		return {
			preferences,
		};
	},
	component: RootLayout,
	errorComponent: (props) => {
		useEffect(() => {
			if (!isTRPCClientError(props.error)) {
				Sentry.captureException(props.error);
			}
		}, [props.error]);

		return (
			<RootDocument>
				<ErrorComponent {...props} />
			</RootDocument>
		);
	},
	notFoundComponent: () => <NotFound />,
	head: () => ({
		meta: [
			{
				charSet: "utf-8",
			},
			{
				name: "viewport",
				content: "width=device-width, initial-scale=1",
			},
			...seo({
				title: "Podcasts by Azerion",
				description:
					"Easily monetize your podcasts with our advanced monetization platform. Publish your podcast or use your existing podcasts to get started quickly.",
				image: "/og.png",
			}),
		],
		links: [
			{ rel: "stylesheet", href: appCss },
			{
				rel: "apple-touch-icon",
				sizes: "180x180",
				href: "/apple-touch-icon.png",
			},
			{
				rel: "icon",
				type: "image/png",
				sizes: "96x96",
				href: "/favicon-96x96.png",
			},
			{ rel: "manifest", href: "/site.webmanifest", color: "#fffff" },
			{ rel: "icon", href: "/favicon.ico" },
			{ rel: "preconnect", href: "https://rsms.me/" },
			{ rel: "stylesheet", href: "https://rsms.me/inter/inter.css" },
		],
	}),
});

function RootLayout() {
	return (
		<RootDocument>
			<Outlet />
		</RootDocument>
	);
}

function RootDocument({ children }: { children: React.ReactNode }) {
	const { i18n } = useLingui();
	const { session } = useSession();

	useEffect(() => {
		if (session.user) {
			Sentry.setUser({
				id: session.user.email,
				email: session.user.email,
				username: session.user.name,
			});
		} else {
			Sentry.setUser(null);
		}
	}, [session.user]);

	return (
		<html className="bg-muted h-full font-sans tracking-tight antialiased" lang={i18n.locale}>
			<head>
				<HeadContent />
			</head>
			<body className="h-full overflow-auto">
				<PreferencesProvider>{children}</PreferencesProvider>
				<Toaster />
				<ReactQueryDevtools position="bottom" buttonPosition="bottom-left" />
				<TanStackRouterDevtools position="bottom-left" />
				<Scripts />
			</body>
		</html>
	);
}
